import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./PricingPageNew.css";

export function PricingPage() {
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  const goStart = (plan) => {
    window.location.href = `/onboarding?plan=${encodeURIComponent(plan)}&billing=${billingPeriod}`;
  };

  const goDemo = () => {
    window.location.href = "/demo";
  };

  // Pricing data
  const plans = {
    receptionist: {
      name: "The Receptionist",
      slug: "receptionist",
      monthlyPrice: 199,
      yearlyPrice: 1990,
      setupFee: 299,
      minutesIncluded: 300,
      overage: 0.50,
      description: "For solo operators & small teams who want after-hours coverage and lead capture.",
      features: [
        "300 minutes/month included",
        "24/7 call answering",
        "Lead capture + call summaries",
        "Basic booking workflows",
        "Email support"
      ],
      bestFor: "after-hours + missed-call protection"
    },
    frontdesk: {
      name: "The Front Desk",
      slug: "frontdesk",
      popular: true,
      monthlyPrice: 349,
      yearlyPrice: 3490,
      setupFee: 499,
      minutesIncluded: 800,
      overage: 0.45,
      description: "For growing clinics & service teams that need reliable booking and cleaner lead conversion.",
      features: [
        "800 minutes/month included",
        "Everything in The Receptionist",
        "Priority booking logic (buffers, providers, complex rules)",
        "SMS follow-ups (optional)",
        "Priority support"
      ],
      bestFor: "busy locations that can't miss calls"
    },
    regional: {
      name: "Regional HQ",
      slug: "regional",
      monthlyPrice: 699,
      yearlyPrice: 6990,
      setupFee: 999,
      minutesIncluded: 2000,
      overage: 0.40,
      description: "For high-volume teams that want advanced workflows and deeper integrations.",
      features: [
        "2,000 minutes/month included",
        "Everything in The Front Desk",
        "Full CRM & EMR integrations (where applicable)",
        "Multi-location routing",
        "Dedicated success manager"
      ],
      bestFor: "multi-location brands and high call volume"
    }
  };

  const getDisplayPrice = (plan) => {
    if (billingPeriod === "yearly") {
      return plan.yearlyPrice;
    }
    return plan.monthlyPrice;
  };

  const getPriceLabel = (plan) => {
    if (billingPeriod === "yearly") {
      return (
        <>
          <div className="amt">${plan.yearlyPrice.toLocaleString()}</div>
          <div className="per">/year</div>
        </>
      );
    }
    return (
      <>
        <div className="amt">${plan.monthlyPrice}</div>
        <div className="per">/month</div>
      </>
    );
  };

  return (
    <div className="pricing-page-container">
      {/* Hero */}
      <section id="pricing-hero" className="hero">
        <div className="container">
          <div className="badge-top">Built for real conversations — not scripts</div>
          <h1>Predictable pricing for a human-quality AI phone agent.</h1>
          <p className="subhead">
            Choose a plan based on usage. Minutes are pooled per account. Upgrade anytime.
            <strong> Setup is mandatory</strong> because we don't launch until your agent sounds natural and follows your rules.
          </p>

          <div className="hero-ctas">
            <button className="btn primary" onClick={() => goStart('frontdesk')}>
              Start My Setup
            </button>
            <button className="btn" onClick={goDemo}>
              Hear Real Calls
            </button>
          </div>

          <div className="trust-row">
            <span>✅ Works with your existing number</span>
            <span>✅ Live in 24–48 hours</span>
            <span>✅ Call logs & recordings available</span>
          </div>
        </div>
      </section>

      {/* Setup Fee */}
      <section id="setup-fee" className="section">
        <div className="container">
          <div className="setup-card">
            <h2>Mandatory Setup (one-time) — White-Glove Onboarding</h2>
            <p>
              Most "AI receptionist" tools give you a dashboard and wish you luck. We don't.
              Your setup fee covers the work needed to launch a reliable agent that callers trust. We custom-build every agent to match your business rules, booking logic, and tone — ensuring human-like calls from day one.
            </p>

            <div className="setup-summary">
              <div className="setup-timeline">
                <span className="timeline-badge">⏱️ Live in 24–48 hours</span>
                <span className="timeline-text">We don't go live until it sounds like your best employee.</span>
              </div>
              
              <div className="setup-includes">
                <div className="include-item">
                  <span className="include-icon">✓</span>
                  <span>Custom call flow & scripts</span>
                </div>
                <div className="include-item">
                  <span className="include-icon">✓</span>
                  <span>Booking logic configuration</span>
                </div>
                <div className="include-item">
                  <span className="include-icon">✓</span>
                  <span>Voice tuning & QA testing</span>
                </div>
              </div>

              <Link to="/features#white-glove-setup" className="setup-details-link">
                See full setup details →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section id="pricing-cards" className="section">
        <div className="container">
          <h2 className="section-title-main">Choose your plan</h2>
          <p className="section-subtitle">
            Plans include minutes. If you exceed your plan, you're billed per extra minute (overage).
            You can set usage alerts so you never get surprised.
          </p>

          {/* Billing Toggle */}
          <div className="billing-toggle-container">
            <div className="billing-toggle">
              <button 
                className={`toggle-btn ${billingPeriod === 'monthly' ? 'active' : ''}`}
                onClick={() => setBillingPeriod('monthly')}
              >
                Monthly
              </button>
              <button 
                className={`toggle-btn ${billingPeriod === 'yearly' ? 'active' : ''}`}
                onClick={() => setBillingPeriod('yearly')}
              >
                Yearly
                <span className="savings-badge">2 months free</span>
              </button>
            </div>
            <p className="billing-note">Save 2 months when you choose annual billing. Setup is paid once.</p>
          </div>

          <div className="cards">
            {/* The Receptionist */}
            <div className="card">
              <div className="plan-name">{plans.receptionist.name}</div>
              <div className="price">
                {getPriceLabel(plans.receptionist)}
              </div>
              {billingPeriod === "yearly" && (
                <div className="yearly-breakdown">
                  ${plans.receptionist.monthlyPrice}/mo billed annually
                </div>
              )}
              <div className="plan-desc">{plans.receptionist.description}</div>

              <ul className="features">
                {plans.receptionist.features.map((feature, idx) => (
                  <li key={idx}>{idx === 0 ? <strong>{feature}</strong> : feature}</li>
                ))}
              </ul>

              <div className="plan-meta-box">
                <div><strong>Overage:</strong> ${plans.receptionist.overage.toFixed(2)} / extra minute</div>
                <div><strong>Setup (one-time):</strong> ${plans.receptionist.setupFee}</div>
              </div>

              <button className="cta" onClick={() => goStart(plans.receptionist.slug)}>
                Start My Setup
              </button>
              <div className="fineprint">Best for: {plans.receptionist.bestFor}.</div>
            </div>

            {/* The Front Desk (Popular) */}
            <div className="card popular">
              <div className="badge">Most Popular</div>
              <div className="plan-name">{plans.frontdesk.name}</div>
              <div className="price">
                {getPriceLabel(plans.frontdesk)}
              </div>
              {billingPeriod === "yearly" && (
                <div className="yearly-breakdown">
                  ${plans.frontdesk.monthlyPrice}/mo billed annually
                </div>
              )}
              <div className="plan-desc">{plans.frontdesk.description}</div>

              <ul className="features">
                {plans.frontdesk.features.map((feature, idx) => (
                  <li key={idx}>{idx === 0 ? <strong>{feature}</strong> : feature}</li>
                ))}
              </ul>

              <div className="plan-meta-box">
                <div><strong>Overage:</strong> ${plans.frontdesk.overage.toFixed(2)} / extra minute</div>
                <div><strong>Setup (one-time):</strong> ${plans.frontdesk.setupFee}</div>
              </div>

              <button className="cta" onClick={() => goStart(plans.frontdesk.slug)}>
                Start My Setup
              </button>
              <div className="fineprint">Best for: {plans.frontdesk.bestFor}.</div>
            </div>

            {/* Regional HQ */}
            <div className="card">
              <div className="plan-name">{plans.regional.name}</div>
              <div className="price">
                {getPriceLabel(plans.regional)}
              </div>
              {billingPeriod === "yearly" && (
                <div className="yearly-breakdown">
                  ${plans.regional.monthlyPrice}/mo billed annually
                </div>
              )}
              <div className="plan-desc">{plans.regional.description}</div>

              <ul className="features">
                {plans.regional.features.map((feature, idx) => (
                  <li key={idx}>{idx === 0 ? <strong>{feature}</strong> : feature}</li>
                ))}
              </ul>

              <div className="plan-meta-box">
                <div><strong>Overage:</strong> ${plans.regional.overage.toFixed(2)} / extra minute</div>
                <div><strong>Setup (one-time):</strong> ${plans.regional.setupFee}</div>
              </div>

              <button className="cta" onClick={() => goStart(plans.regional.slug)}>
                Start My Setup
              </button>
              <div className="fineprint">Best for: {plans.regional.bestFor}.</div>
            </div>
          </div>

          <div className="compare-link">
            <a href="#compare">See full plan comparison ↓</a>
          </div>
        </div>
      </section>

      {/* Billing Explained */}
      <section id="billing-explained" className="section">
        <div className="container">
          <h2 className="section-title-main">No surprises. Here's exactly how billing works.</h2>

          <div className="nosurprises">
            <div className="ns">
              <h3>What counts as a "minute"?</h3>
              <p>
                Minutes are measured by live call time while the agent is active on the call.
                If a call lasts 2 minutes 10 seconds, it counts as 3 minutes.
              </p>
            </div>

            <div className="ns">
              <h3>What happens if I go over?</h3>
              <p>
                If you exceed your included minutes, you're charged per extra minute at your plan's overage rate.
                You'll get warnings at 70%, 90%, and 100% so there are no surprises.
              </p>
            </div>

            <div className="ns">
              <h3>Can I upgrade mid-cycle?</h3>
              <p>
                Yes. We prorate your upgrade immediately, and unused minutes carry forward.
                Downgrades apply at the end of your billing cycle.
              </p>
            </div>

            <div className="ns">
              <h3>Is the setup fee required?</h3>
              <p>
                Yes. The setup fee is mandatory and one-time regardless of billing cycle.
                It covers custom configuration, voice tuning, and QA testing before launch.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Full Plan Comparison */}
      <section id="compare" className="section">
        <div className="container">
          <h2 className="section-title-main">Full Plan Comparison</h2>
          
          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>The Receptionist</th>
                  <th className="popular-col">The Front Desk</th>
                  <th>Regional HQ</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Monthly Price</td>
                  <td>$199/mo</td>
                  <td className="popular-col">$349/mo</td>
                  <td>$699/mo</td>
                </tr>
                <tr>
                  <td>Yearly Price</td>
                  <td>$1,990/yr (2 mo free)</td>
                  <td className="popular-col">$3,490/yr (2 mo free)</td>
                  <td>$6,990/yr (2 mo free)</td>
                </tr>
                <tr>
                  <td>Setup Fee (one-time)</td>
                  <td>$299</td>
                  <td className="popular-col">$499</td>
                  <td>$999</td>
                </tr>
                <tr>
                  <td>Minutes Included</td>
                  <td>300/month</td>
                  <td className="popular-col">800/month</td>
                  <td>2,000/month</td>
                </tr>
                <tr>
                  <td>Overage Rate</td>
                  <td>$0.50/min</td>
                  <td className="popular-col">$0.45/min</td>
                  <td>$0.40/min</td>
                </tr>
                <tr>
                  <td>24/7 Call Answering</td>
                  <td>✓</td>
                  <td className="popular-col">✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Lead Capture</td>
                  <td>✓</td>
                  <td className="popular-col">✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Call Summaries</td>
                  <td>✓</td>
                  <td className="popular-col">✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Basic Booking</td>
                  <td>✓</td>
                  <td className="popular-col">✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Priority Booking Logic</td>
                  <td>—</td>
                  <td className="popular-col">✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>SMS Follow-ups</td>
                  <td>—</td>
                  <td className="popular-col">✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>CRM/EMR Integrations</td>
                  <td>—</td>
                  <td className="popular-col">—</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Multi-location Routing</td>
                  <td>—</td>
                  <td className="popular-col">—</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Support</td>
                  <td>Email</td>
                  <td className="popular-col">Priority</td>
                  <td>Dedicated Manager</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section id="roi-section" className="section">
        <div className="container roi">
          <h2 className="section-title-main">The math is simple.</h2>
          <p className="section-subtitle">
            The average missed call costs $150+ in lost revenue. One converted call pays for most plans.
          </p>

          <div className="examples">
            <div className="ex">
              <div className="icon">🦷</div>
              <h4>Dental Clinic</h4>
              <p>
                Missed 20 calls/month × $200 avg value = <strong>$4,000 lost</strong>.
                Calyro cost: ~$349. ROI: <span className="highlight">11x</span>
              </p>
            </div>
            <div className="ex">
              <div className="icon">❄️</div>
              <h4>HVAC Contractor</h4>
              <p>
                Emergency calls after-hours × $400 job value = massive leak.
                Calyro captures them 24/7.
              </p>
            </div>
            <div className="ex">
              <div className="icon">💆</div>
              <h4>Med Spa</h4>
              <p>
                High-value consultations often lost to slow response.
                Calyro books while interest is hot.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="final-cta" className="section">
        <div className="container final-cta-inner">
          <h2>Ready to stop losing calls?</h2>
          <p>Start your setup now. Live in 24–48 hours.</p>
          <div className="final-btns">
            <button className="btn primary" onClick={() => goStart('frontdesk')}>
              Start My Setup
            </button>
            <button className="btn" onClick={goDemo}>
              Book Onboarding Call
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
